/**
 * Utility functions for managing favicon dynamically
 */

import { getImageUrl } from './imageUtils';

/**
 * Update the favicon link in the document head
 * @param faviconUrl - URL or path to the favicon image
 */
export const updateFavicon = (faviconUrl: string | null | undefined): void => {
  if (typeof document === 'undefined') return;

  if (!faviconUrl) {
    // Reset to default favicon if no URL provided
    const defaultFavicon = '/favicon/favicon-192.webp';
    setFavicon(defaultFavicon);
    return;
  }

  // Get the full URL for the favicon
  const fullFaviconUrl = getImageUrl(faviconUrl);
  if (!fullFaviconUrl) return;

  setFavicon(fullFaviconUrl);
};

/**
 * Set the favicon in the document head
 * @param faviconUrl - Full URL to the favicon
 */
const setFavicon = (faviconUrl: string): void => {
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach(link => link.remove());

  // Create new favicon link
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href = faviconUrl;

  // Add to head
  document.head.appendChild(link);

  // Also update apple-touch-icon for better mobile support
  const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
  if (appleTouchIcon) {
    appleTouchIcon.setAttribute('href', faviconUrl);
  } else {
    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = faviconUrl;
    document.head.appendChild(appleLink);
  }

  // Force browser to reload favicon by adding timestamp to bypass cache
  // This helps with browser caching issues
  const timestamp = new Date().getTime();
  const separator = faviconUrl.includes('?') ? '&' : '?';
  link.href = `${faviconUrl}${separator}v=${timestamp}`;
};

